import React, { useState, useEffect, useMemo } from 'react';
import { Disc3, Plus, Search, Edit, Trash2, X, Save, PackageCheck, ArrowRightLeft, Calendar, Snowflake, Sun, CircleDot } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';

const SEIZOENEN = ['Winter', 'Zomer', 'All-season'];
const STATUSSEN = ['Ingeslagen', 'Uitgegeven'];
const seizoenMeta = (s) => s === 'Winter' ? { icon: Snowflake, c: '#2563eb', bg: '#eff6ff' }
  : s === 'Zomer' ? { icon: Sun, c: '#ea580c', bg: '#fff7ed' }
  : { icon: CircleDot, c: '#16a34a', bg: '#f0fdf4' };
const fmt = (d) => d ? new Date(d).toLocaleDateString('nl-NL') : '—';

// Wisseltip: winterbanden in maart/april → tijd voor zomer; zomerbanden in sep/okt → tijd voor winter
const wisselTip = (bs) => {
  if (bs.status !== 'Ingeslagen') return null;
  const m = new Date().getMonth() + 1;
  if (bs.seizoen === 'Winter' && (m === 3 || m === 4)) return 'Zomerwissel';
  if (bs.seizoen === 'Zomer' && (m === 9 || m === 10)) return 'Winterwissel';
  return null;
};

export default function Bandenhotel({ onNavigate }) {
  const { t } = useT();
  const { klanten, klantAutos } = useData();
  const [sets, setSets] = useState([]);
  const [q, setQ] = useState('');
  const [seizoenFilter, setSeizoenFilter] = useState('Alle');
  const [editing, setEditing] = useState(null);

  const load = () => carsysApi.getBandensets().then(setSets).catch(() => setSets([]));
  useEffect(() => { load(); }, []);

  const klantById = useMemo(() => Object.fromEntries((klanten || []).map(k => [k.id, k])), [klanten]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return sets.filter(b => {
      if (seizoenFilter !== 'Alle' && b.seizoen !== seizoenFilter) return false;
      if (!s) return true;
      return `${b.kenteken} ${b.merk} ${b.maat} ${b.locatie} ${klantById[b.klantId]?.naam || ''}`.toLowerCase().includes(s);
    });
  }, [sets, q, seizoenFilter, klantById]);

  const inStock = sets.filter(b => b.status === 'Ingeslagen').length;
  const tips = sets.filter(b => wisselTip(b)).length;

  const del = async (id) => {
    if (!(await confirmDialog(t('Bandenset verwijderen?', 'Bandenset verwijderen?')))) return;
    await carsysApi.deleteBandenset(id); setSets(prev => prev.filter(b => b.id !== id));
    toast.success(t('Verwijderd', 'Verwijderd'));
  };
  const toggleStatus = async (b) => {
    const nieuw = b.status === 'Ingeslagen' ? 'Uitgegeven' : 'Ingeslagen';
    const upd = await carsysApi.updateBandenset(b.id, { ...b, status: nieuw, uitgegevenDatum: nieuw === 'Uitgegeven' ? new Date().toISOString().slice(0, 10) : '' });
    setSets(prev => prev.map(x => x.id === b.id ? upd : x));
    toast.success(nieuw === 'Uitgegeven' ? t('Banden uitgegeven', 'Banden uitgegeven') : t('Terug op voorraad', 'Terug op voorraad'));
  };

  return (
    <div className="space-y-4" data-testid="bandenhotel-page">
      <div className="cs-page-header">
        <div><h2><Disc3 size={20} style={{ verticalAlign: -3, marginRight: 6 }} />{t('Bandenhotel', 'Bandenhotel')}</h2>
          <p>{inStock} {t('sets in opslag', 'sets in opslag')} · {tips} {t('wisseltip(s)', 'wisseltip(s)')}</p></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
            <input className="cs-input" style={{ paddingLeft: 28, width: 200 }} placeholder={t('Zoek kenteken/locatie/klant', 'Zoek kenteken/locatie/klant')} value={q} onChange={e => setQ(e.target.value)} data-testid="bh-search" />
          </div>
          <select className="cs-input" style={{ width: 130 }} value={seizoenFilter} onChange={e => setSeizoenFilter(e.target.value)} data-testid="bh-filter-seizoen">
            <option value="Alle">{t('Alle seizoenen', 'Alle seizoenen')}</option>
            {SEIZOENEN.map(s => <option key={s} value={s}>{t(s, s)}</option>)}
          </select>
          <button className="cs-btn primary" onClick={() => setEditing({})} data-testid="bh-add"><Plus size={14} /> {t('Banden inslaan', 'Banden inslaan')}</button>
        </div>
      </div>

      <div className="cs-card" data-testid="bh-list">
        <table className="cs-table">
          <thead><tr><th>{t('Kenteken')}</th><th>{t('Klant')}</th><th>{t('Seizoen', 'Seizoen')}</th><th>{t('Bandenmaat', 'Bandenmaat')}</th><th>{t('Profiel', 'Profiel')}</th><th>{t('Locatie', 'Locatie')}</th><th>{t('Status')}</th><th></th></tr></thead>
          <tbody>
            {filtered.map(b => {
              const sm = seizoenMeta(b.seizoen); const Icon = sm.icon; const klant = klantById[b.klantId]; const tip = wisselTip(b);
              return (
                <tr key={b.id} data-testid={`bh-row-${b.id}`}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{b.kenteken || '—'}</td>
                  <td>{klant?.naam || b.klantNaam || '—'}</td>
                  <td><span className="cs-badge" style={{ background: sm.bg, color: sm.c, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon size={12} /> {t(b.seizoen, b.seizoen)}</span>
                    {tip && <span className="cs-badge" style={{ background: '#fef2f2', color: '#b91c1c', marginLeft: 4 }} data-testid={`bh-tip-${b.id}`}>{t(tip, tip)}</span>}</td>
                  <td>{b.maat || '—'} {b.aantal ? `(${b.aantal}×)` : ''}</td>
                  <td>{b.profielMm ? `${b.profielMm} mm` : '—'}</td>
                  <td style={{ fontWeight: 600 }}>{b.locatie || '—'}</td>
                  <td><span className="cs-badge" style={{ background: b.status === 'Ingeslagen' ? '#f0fdf4' : '#f1f5f9', color: b.status === 'Ingeslagen' ? '#15803d' : '#64748b' }} data-testid={`bh-status-${b.id}`}>{t(b.status, b.status)}</span></td>
                  <td><div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button className="cs-btn ghost" title={t('Plan seizoenswissel', 'Plan seizoenswissel')} onClick={() => onNavigate && onNavigate('planning')} data-testid={`bh-plan-${b.id}`}><Calendar size={15} /></button>
                    <button className="cs-btn ghost" title={b.status === 'Ingeslagen' ? t('Banden uitgeven', 'Banden uitgeven') : t('Weer inslaan', 'Weer inslaan')} onClick={() => toggleStatus(b)} data-testid={`bh-toggle-${b.id}`}><ArrowRightLeft size={15} /></button>
                    <button className="cs-btn ghost" onClick={() => setEditing(b)} data-testid={`bh-edit-${b.id}`}><Edit size={15} /></button>
                    <button className="cs-btn ghost" onClick={() => del(b.id)} data-testid={`bh-del-${b.id}`}><Trash2 size={15} color="#dc2626" /></button>
                  </div></td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={8} className="cs-empty">{t('Nog geen bandensets opgeslagen', 'Nog geen bandensets opgeslagen')}</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <BandensetEditor set={editing} klanten={klanten} klantAutos={klantAutos} onClose={() => setEditing(null)} onSaved={(b, isNew) => { setSets(prev => isNew ? [...prev, b] : prev.map(x => x.id === b.id ? b : x)); setEditing(null); }} t={t} />}
    </div>
  );
}

function BandensetEditor({ set, klanten, klantAutos, onClose, onSaved, t }) {
  const isNew = !set.id;
  const [d, setD] = useState(() => ({
    klantId: '', kenteken: '', seizoen: 'Winter', merk: '', maat: '', aantal: 4, profielMm: '', dot: '', locatie: '', opmerkingen: '', status: 'Ingeslagen', ...set,
  }));
  const u = (k, v) => setD(p => ({ ...p, [k]: v }));
  // Auto-koppel kenteken → klant
  const onKenteken = (v) => {
    const kt = v.toUpperCase();
    const auto = (klantAutos || []).find(a => a.kenteken === kt);
    setD(p => ({ ...p, kenteken: kt, klantId: auto?.klantId || p.klantId, maat: p.maat || '', merk: p.merk }));
  };
  const save = async () => {
    if (!d.locatie?.trim()) { toast.error(t('Locatie is verplicht', 'Locatie is verplicht')); return; }
    try {
      const payload = { ...d, aantal: parseInt(d.aantal) || 0 };
      const res = isNew ? await carsysApi.createBandenset(payload) : await carsysApi.updateBandenset(set.id, payload);
      toast.success(t('Opgeslagen', 'Opgeslagen'));
      onSaved(res, isNew);
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }} data-testid="bandenset-editor">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}><PackageCheck size={17} style={{ verticalAlign: -3, marginRight: 5 }} />{isNew ? t('Banden inslaan', 'Banden inslaan') : t('Bandenset bewerken', 'Bandenset bewerken')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label className="cs-section-title">{t('Kenteken')}</label><input className="cs-input" value={d.kenteken} onChange={e => onKenteken(e.target.value)} placeholder="12-AB-34" style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }} data-testid="bs-kenteken" /></div>
          <div><label className="cs-section-title">{t('Klant')}</label>
            <select className="cs-input" value={d.klantId} onChange={e => u('klantId', e.target.value)} data-testid="bs-klant">
              <option value="">-- {t('Geen', 'Geen')} --</option>
              {(klanten || []).map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
            </select></div>
          <div><label className="cs-section-title">{t('Seizoen', 'Seizoen')}</label><select className="cs-input" value={d.seizoen} onChange={e => u('seizoen', e.target.value)} data-testid="bs-seizoen">{SEIZOENEN.map(s => <option key={s} value={s}>{t(s, s)}</option>)}</select></div>
          <div><label className="cs-section-title">{t('Merk')}</label><input className="cs-input" value={d.merk} onChange={e => u('merk', e.target.value)} placeholder="Michelin / Continental…" /></div>
          <div><label className="cs-section-title">{t('Bandenmaat', 'Bandenmaat')}</label><input className="cs-input" value={d.maat} onChange={e => u('maat', e.target.value)} placeholder="205/55 R16" data-testid="bs-maat" /></div>
          <div><label className="cs-section-title">{t('Aantal', 'Aantal')}</label><input type="number" className="cs-input" value={d.aantal} onChange={e => u('aantal', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Profieldiepte (mm)', 'Profieldiepte (mm)')}</label><input type="number" step="0.1" className="cs-input" value={d.profielMm} onChange={e => u('profielMm', e.target.value)} placeholder="6,5" /></div>
          <div><label className="cs-section-title">{t('DOT / bouwweek', 'DOT / bouwweek')}</label><input className="cs-input" value={d.dot} onChange={e => u('dot', e.target.value)} placeholder="2419" /></div>
          <div><label className="cs-section-title">{t('Opslaglocatie', 'Opslaglocatie')} *</label><input className="cs-input" value={d.locatie} onChange={e => u('locatie', e.target.value)} placeholder="Stelling A-12" data-testid="bs-locatie" /></div>
          <div><label className="cs-section-title">{t('Status')}</label><select className="cs-input" value={d.status} onChange={e => u('status', e.target.value)}>{STATUSSEN.map(s => <option key={s} value={s}>{t(s, s)}</option>)}</select></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Opmerkingen')}</label><textarea className="cs-input" rows={2} value={d.opmerkingen} onChange={e => u('opmerkingen', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={save} data-testid="bs-save"><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}
